import { useState, useEffect } from 'react';
import { FaAmazon, FaCheckCircle, FaExclamationCircle, FaExternalLinkAlt } from 'react-icons/fa';
import api from '../../services/api';

const Step2SPAPICredentials = ({ onNext, onBack }) => {
  const [credentials, setCredentials] = useState({
    sellerId: '',
    marketplaceId: '',
    refreshToken: '',
    clientId: '',
    clientSecret: ''
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    checkExistingCredentials();
  }, []);

  const checkExistingCredentials = async () => {
    try {
      const { data } = await api.get('/credentials/amazon');
      if (data.data.configured) {
        setIsConfigured(true);
        setSuccess('Amazon credentials already configured');
      }
    } catch (err) {
      // No credentials yet
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    // Validar que todos los campos estén llenos
    if (!credentials.sellerId || !credentials.marketplaceId || !credentials.refreshToken || 
        !credentials.clientId || !credentials.clientSecret) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/credentials/amazon', credentials);
      console.log('Credentials saved:', response.data);
      setSuccess('Credentials saved successfully! ✅');
      setIsConfigured(true);
      
      // Clear form for security
      setCredentials({
        sellerId: '',
        marketplaceId: '',
        refreshToken: '',
        clientId: '',
        clientSecret: ''
      });
    } catch (err) {
      console.error('Error saving credentials:', err);
      setError(err.response?.data?.error || 'Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/credentials/amazon/test');
      setSuccess('Connection validated successfully! ✅');
    } catch (err) {
      setError(err.response?.data?.error || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleContinue = () => {
    if (!isConfigured) {
      setError('Please configure your Amazon credentials first');
      return;
    }
    onNext({ spApiConfigured: true });
  };

  const marketplaceIds = [
    { country: 'United States', id: 'ATVPDKIKX0DER' },
    { country: 'Canada', id: 'A2EUQ1WTGCTBG2' },
    { country: 'Mexico', id: 'A1AM78C64UM0Y8' },
    { country: 'Brazil', id: 'A2Q3Y263D00KWC' },
    { country: 'United Kingdom', id: 'A1F83G8C2ARO7P' },
    { country: 'Germany', id: 'A1PA6795UKMFR9' },
    { country: 'France', id: 'A13V1IB3VIYZZH' },
    { country: 'Italy', id: 'APJ6JRA9NG5V4' },
    { country: 'Spain', id: 'A1RKKUPIHCS9HS' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
          <FaAmazon className="text-3xl text-orange-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Connect Your Amazon Account
        </h2>
        <p className="text-gray-600">
          Enter your Amazon SP-API credentials to sync your products and orders
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded">
          <div className="flex items-center">
            <FaExclamationCircle className="text-red-500 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded">
          <div className="flex items-center">
            <FaCheckCircle className="text-green-500 mr-2" />
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Credentials Form */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seller ID
          </label>
          <input
            type="text"
            name="sellerId"
            value={credentials.sellerId}
            onChange={handleChange}
            placeholder="A1B2C3D4E5F6G"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marketplace ID
          </label>
          <select
            name="marketplaceId"
            value={credentials.marketplaceId}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select your marketplace</option>
            {marketplaceIds.map((mp) => (
              <option key={mp.id} value={mp.id}>
                {mp.country} - {mp.id}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Refresh Token
          </label>
          <input
            type="password"
            name="refreshToken"
            value={credentials.refreshToken}
            onChange={handleChange}
            placeholder="Atzr|IwEBIA..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Client ID (LWA)
          </label>
          <input
            type="text"
            name="clientId"
            value={credentials.clientId}
            onChange={handleChange}
            placeholder="amzn1.application-oa2-client.xxxxx"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Client Secret (LWA)
          </label>
          <input
            type="password"
            name="clientSecret"
            value={credentials.clientSecret}
            onChange={handleChange}
            placeholder="Enter your client secret"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={handleSave}
          disabled={loading || !credentials.sellerId || !credentials.marketplaceId}
          className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Saving...' : 'Save Credentials'}
        </button>
        {isConfigured && (
          <button
            onClick={handleValidate}
            disabled={validating}
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {validating ? 'Validating...' : 'Validate'}
          </button>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-blue-900 mb-2">
          Need help getting your credentials?
        </h4>
        <ul className="text-sm text-blue-800 space-y-1 mb-3">
          <li>• Seller ID: Found in Seller Central → Settings → Account Info</li>
          <li>• Marketplace ID: Select your country from the dropdown above</li>
          <li>• Client ID & Secret: Create an app in Amazon Developer Console</li>
          <li>• Refresh Token: Authorize your app with Amazon</li>
        </ul>
        <a
          href="https://developer.amazonservices.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          Open Amazon Developer Console
          <FaExternalLinkAlt className="ml-2 text-sm" />
        </a>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          ← Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!isConfigured}
          className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          Continue →
        </button>
      </div>
    </div>
  );
};

export default Step2SPAPICredentials;
